import { FC, useMemo, useRef, useEffect } from 'react';
import Map, { Marker, MapRef, GeolocateControl } from 'react-map-gl/mapbox';
import { MAPBOX_ACCESS_TOKEN } from '../lib/map/token';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Stop, Day, StopStateEnum } from "@city-sage/core-client";
import { MapPin } from "lucide-react";
import { DAY_COLOR_HEX } from '../lib/constants/dayColors';
import { useQuery } from '@tanstack/react-query';
import { useRepository } from '@city-sage/aurum-avis-labs-components';
import { QueryKeys } from '../lib/QueryKeys';

interface MiniItineraryMapProps {
  days: Day[] | undefined;
  itineraryId: string;
  onStopClick?: (stop: Stop) => void;
}

export const MiniItineraryMap: FC<MiniItineraryMapProps> = ({ days, itineraryId, onStopClick }) => {
  const mapRef = useRef<MapRef | null>(null);

  const { api } = useRepository();
  const { data: stops } = useQuery({
    queryKey: [QueryKeys.itinerary.stops(itineraryId, undefined, [StopStateEnum.Accepted, StopStateEnum.Suggested])],
    queryFn: () => api.findAllItineraryStop({ itineraryId: itineraryId,filter: JSON.stringify({state: [StopStateEnum.Accepted, StopStateEnum.Suggested]}), sort: JSON.stringify(["index", "ASC"]),
      // TODO this is hardcoded for now, we need to fix this
      limit: 1000,
    }),
  });


  // Calculate bounds
  const bounds = useMemo(() => {
    if (stops?.length === 0) return null;
    
    const lngs = stops?.map(stop => stop.place.coordinates!.longitude!) ?? [];
    const lats = stops?.map(stop => stop.place.coordinates!.latitude!) ?? [];
    
    return {
      minLng: Math.min(...lngs),
      maxLng: Math.max(...lngs),
      minLat: Math.min(...lats),
      maxLat: Math.max(...lats)
    };
  }, [stops]);

  // Calculate initial view state
  const initialViewState = useMemo(() => {
    if (stops?.length === 0) {
      return {
        longitude: 0,
        latitude: 20,
        zoom: 1
      };
    }
    
    if (stops?.length === 1) {
      return {
        longitude: stops?.[0].place.coordinates!.longitude!,
        latitude: stops?.[0].place.coordinates!.latitude!,
        zoom: 14
      };
    }
    
    if (bounds) {
      return {
        longitude: (bounds.minLng + bounds.maxLng) / 2,
        latitude: (bounds.minLat + bounds.maxLat) / 2,
        zoom: 10
      };
    }
    
    return {
      longitude: 0,
      latitude: 20,
      zoom: 1
    };
  }, [stops, bounds]);

  // Fit bounds when map loads
  useEffect(() => {
    if (mapRef.current && bounds && stops?.length && stops?.length > 1) {
      const map = mapRef.current.getMap();
      
      setTimeout(() => {
        map.fitBounds(
          [
            [bounds.minLng, bounds.minLat],
            [bounds.maxLng, bounds.maxLat]
          ],
          {
            padding: { top: 40, bottom: 40, left: 40, right: 40 },
            duration: 1000,
            maxZoom: 16
          }
        );
      }, 100);
    } else if (mapRef.current && stops?.length === 1) {
      const map = mapRef.current.getMap();
      const stop = stops?.[0];
      
      setTimeout(() => {
        map.flyTo({
          center: [stop.place.coordinates!.longitude, stop.place.coordinates!.latitude],
          zoom: 14,
          duration: 1000,
          essential: true
        });
      }, 100);
    }
  }, [bounds, stops]);

  if (stops?.length === 0) {
    return (
      <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
        <div className="text-center text-gray-500">
          <MapPin className="h-8 w-8 mx-auto mb-2" />
          <p className="text-sm">Add stops to see map preview</p>
        </div>
      </div>
    );
  }

  return (
    <div className="aspect-video rounded-lg overflow-hidden">
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/streets-v12"
        initialViewState={initialViewState}
        interactive={true}
      >
        {/* Render stop markers */}
        {stops?.map((stop, index) => {
          const day = days?.find(day => day.stopIds?.includes(stop.id));
          const dayColor = day ? DAY_COLOR_HEX[day.number - 1] : '#6366f1'; // fallback color
          
          return (
            <Marker
              key={stop.id}
              longitude={stop.place.coordinates?.longitude}
              latitude={stop.place.coordinates?.latitude}
              anchor="center"
            >
              <div
                className="w-6 h-6 rounded-full border-2 border-white shadow-lg flex items-center justify-center hover:scale-110 transition-transform duration-200"
                style={{ backgroundColor: dayColor }}
                title={stop.title || `Stop ${index + 1}`}
                onClick={() => onStopClick?.(stop)}
              >
                <span className="text-xs font-bold text-white">{index + 1}</span>
              </div>
            </Marker>
          );
        })}

        {/* Geolocation Control */}
        <GeolocateControl
          position="top-right"
          positionOptions={{ enableHighAccuracy: true }}
          trackUserLocation={true}
          showAccuracyCircle={false}
        />
      </Map>
    </div>
  );
}; 